import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AvailableCharitiesModal, DonateToCharityModal } from '@hero/components/modals';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { FEATURE_TYPES } from '@hero/constants';
import { useGetCharity } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { ChooseFeatureTypeModal, ProductSelectionSection } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useShoppingCart } from '@hero/store/hooks';

const ShoppingButton = styled(IconButton)`
  border-color: var(--color-border-default);
  height: 48px;
  width: 48px;
`;

const ShoppingButtonBadge = styled.span`
  align-items: center;
  background: var(--color-components-button-primary-outlined-fg);
  border-radius: var(--size-border-radius-circle);
  color: var(--color-components-button-primary-outlined-bg);
  display: flex;
  font-size: 14px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  line-height: 150%;
  position: absolute;
  right: -10px;
  text-align: center;
  top: -10px;
  width: 24px;
`;

export default function GiftCatalog({ pageData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { products, updateProducts, setOpened } = useShoppingCart();
  const { charityEnabled, isCharityExist, anyCharityItemExists } = useGetCharity();

  const hasCharity = anyCharityItemExists(products);

  const [showCharityInfoModal, setShowCharityInfoModal] = useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = useState(false);
  const [chooseFeatureModalIsOpen, setChooseFeatureModalIsOpen] = useState(false);
  const { addError } = useValidationErrorsStore();

  const onBack = () => navigate(ROUTE_PATHS.HOME);
  const onNext = () => {
    if (products.length === 0) {
      addError(
        <Trans
          key="noProductAtList"
          i18nKey="errors.noSelectedProduct"
          components={{
            b: <b />
          }}
        />
      );

      return;
    }

    if (charityEnabled && !hasCharity && isCharityExist) {
      return setShowCharityInfoModal(true);
    }
    return setChooseFeatureModalIsOpen(true);
  };

  return (
    <PageLayout
      pageData={pageData}
      header={{
        title: t('giftCatalog'),
        step: 1,
        backButton: {
          onClick: onBack
        },
        components: {
          EndContent: () => (
            <ShoppingButton
              name="shopping-cart"
              size="lg"
              variant="outlined"
              rounded={true}
              onClick={() => setOpened(true)}
              style={{ position: 'relative' }}
            >
              <ShoppingButtonBadge>{products.length}</ShoppingButtonBadge>
              <Icon name="shopping-cart" size="lg" />
            </ShoppingButton>
          )
        }
      }}
      navigation={{
        leftButton: {
          children: t('back'),
          onClick: onBack
        },
        rightButton: {
          icon: true,
          children: t('continueWithSelectedGifts'),
          onClick: onNext
        }
      }}
    >
      <ProductSelectionSection
        selectedProducts={products}
        onChangeSelectedProductList={selectedProducts => {
          updateProducts(selectedProducts);
        }}
        isDraft
        featureType={FEATURE_TYPES.recognition}
      />
      <ChooseFeatureTypeModal
        isOpen={chooseFeatureModalIsOpen}
        onClose={() => {
          setChooseFeatureModalIsOpen(false);
        }}
      />
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setChooseFeatureModalIsOpen(true);
            },
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          setSelectedCharities={selectedCharities =>
            updateProducts([...products, ...selectedCharities])
          }
        />
      )}
    </PageLayout>
  );
}
